import { LockClosedIcon } from "@heroicons/react/24/solid";
import { get, isEmpty } from "lodash";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "urql";
import { loader } from "graphql.macro";
import { useAppSelector } from "../../app/hooks";
import { FormInput } from "../../components/Form/FormInput";
import { FormLabel } from "../../components/Form/FormLabel";
import { AvoRedApp } from "../../components/Layout/AvoRedApp";
import { getAuthUserInfo } from "../../features/userLogin/userLoginSlice";
import { UserSidebar } from "./UserSidebar";
import { setMessage } from "../../features/flash/flashSlice";
import { useAppDispatch } from "../../app/hooks";
import { DebugGraphqlErrorMessage } from "../../components/DebugGraphqlErrorMessage";

const ChangePasswordQuery = loader(
  "../../graphql/mutation/auth/ChangePassword.graphql"
);

export const ChangePassword = () => {
  const currentUserInfo = useAppSelector(getAuthUserInfo);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [changePasswordResult, changePasswordMutation] =
    useMutation(ChangePasswordQuery);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [formValidation, setFormValidation] = useState<
    Record<string, string[]>
  >({});
  const [generalError, setGeneralError] = useState("");
  const [debugMessage, setDebugMessage] = useState("");

  const { fetching } = changePasswordResult;

  const currentPasswordOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
  };

  const newPasswordOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
  };

  const newPasswordConfirmationOnChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewPasswordConfirmation(e.target.value);
  };

  const submitHandle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset error states
    setDebugMessage("");
    setFormValidation({});
    setGeneralError("");

    // Client-side validation
    if (newPassword !== newPasswordConfirmation) {
      setGeneralError("Xác nhận mật khẩu mới không khớp");
      return;
    }

    if (newPassword.length < 8) {
      setGeneralError("Mật khẩu mới phải có ít nhất 8 ký tự");
      return;
    }

    const variables = {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    };

    try {
      const mutationResult = await changePasswordMutation(variables);

      // Handle GraphQL errors
      if (mutationResult.error) {
        // Check for debug message
        const debugMsg = get(
          mutationResult,
          "error.graphQLErrors[0].originalError.debugMessage",
          ""
        );
        if (!isEmpty(debugMsg)) {
          setDebugMessage(debugMsg);
        }

        // Check for validation errors
        const validation = get(
          mutationResult,
          "error.graphQLErrors.0.extensions.validation",
          {}
        );
        if (!isEmpty(validation)) {
          setFormValidation(validation);
        }

        // Check for general GraphQL error message
        const graphQLError = get(
          mutationResult,
          "error.graphQLErrors[0].message",
          ""
        );
        const networkError = get(
          mutationResult,
          "error.networkError.message",
          ""
        );

        if (
          !isEmpty(graphQLError) &&
          isEmpty(debugMsg) &&
          isEmpty(validation)
        ) {
          setGeneralError(graphQLError);
        } else if (!isEmpty(networkError)) {
          setGeneralError(networkError);
        } else if (mutationResult.error.message) {
          setGeneralError(mutationResult.error.message);
        }

        return;
      }

      // Handle successful password change
      const success = get(mutationResult, "data.changePassword.success", false);
      const message = get(mutationResult, "data.changePassword.message", "");

      if (success) {
        dispatch(setMessage(message || "Mật khẩu đã được thay đổi thành công"));
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setNewPasswordConfirmation("");
        // Navigate back to profile
        setTimeout(() => {
          navigate("/user/profile");
        }, 1500);
      }
    } catch (error: any) {
      // Handle unexpected errors
      setGeneralError(
        error.message || "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại."
      );
    }
  };

  return (
    <AvoRedApp>
      {fetching ? (
        <div className="min-h-screen bg-gray-50">
          {/* Breadcrumb Skeleton */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-4 text-gray-300">/</div>
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </nav>

          {/* Content Skeleton */}
          <section className="bg-gray-50 py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                <div className="space-y-4">
                  <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <>
          {/* Breadcrumb Navigation */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center space-x-2 text-sm overflow-x-auto">
                <Link
                  to="/"
                  className="text-gray-500 hover:text-indigo-600 transition-colors duration-200 flex items-center whitespace-nowrap"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Trang chủ
                </Link>
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-gray-500 whitespace-nowrap">Hồ sơ</span>
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-gray-900 font-semibold truncate max-w-xs md:max-w-md">
                  Đổi mật khẩu
                </span>
              </div>
            </div>
          </nav>

          <div className="bg-white shadow">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Đổi mật khẩu
            </h1>
              <p className="mt-2 text-gray-600">
                Bảo mật tài khoản của bạn bằng cách thay đổi mật khẩu định kỳ
              </p>
            </div>
          </div>

          <section className="bg-gray-50 py-8 md:py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="grid gap-8 lg:grid-cols-4 lg:gap-12">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <UserSidebar user={currentUserInfo} />
                  </div>
                </div>

                {/* Main Content */}
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Form Content */}
                    <form className="p-6 lg:p-8" onSubmit={submitHandle}>
                      <div className="space-y-6">
                        {/* Security Section */}
                        <div>
                          <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
                            <svg
                              className="w-6 h-6 text-blue-600 mr-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                            <h2 className="text-xl font-bold text-gray-900">
                              Bảo mật tài khoản
                            </h2>
                          </div>

                          <DebugGraphqlErrorMessage message={debugMessage} />

                          {generalError && (
                            <div className="mb-6 bg-red-50 border border-red-200 text-sm py-3 px-5 rounded-lg text-red-700">
                              {generalError}
                            </div>
                          )}

                          <div className="grid gap-6 md:grid-cols-1">
                            {/* Current Password */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                  />
                                </svg>
                                <FormLabel
                                  forId="current-password"
                                  labelText="Mật khẩu hiện tại"
                                />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  id="current-password"
                                  type="password"
                                  value={currentPassword}
                                  setOnChange={currentPasswordOnChange}
                                  errorMessages={get(
                                    formValidation,
                                    "current_password",
                                    []
                                  )}
                                  placeholder="Nhập mật khẩu hiện tại"
                                />
                              </div>
                            </div>

                            {/* New Password */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                  />
                                </svg>
                                <FormLabel
                                  forId="new-password"
                                  labelText="Mật khẩu mới"
                                />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  id="new-password"
                                  type="password"
                                  value={newPassword}
                                  setOnChange={newPasswordOnChange}
                                  errorMessages={get(
                                    formValidation,
                                    "new_password",
                                    []
                                  )}
                                  placeholder="Nhập mật khẩu mới (tối thiểu 8 ký tự)"
                                />
                              </div>
                            </div>

                            {/* Confirm New Password */}
                            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                              <div className="flex items-center mb-2">
                                <svg
                                  className="w-5 h-5 text-gray-400 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <FormLabel
                                  forId="new-password-confirmation"
                                  labelText="Xác nhận mật khẩu mới"
                                />
                              </div>
                              <div className="mt-2">
                                <FormInput
                                  id="new-password-confirmation"
                                  type="password"
                                  value={newPasswordConfirmation}
                                  setOnChange={newPasswordConfirmationOnChange}
                                  errorMessages={get(
                                    formValidation,
                                    "new_password_confirmation",
                                    []
                                  )}
                                  placeholder="Nhập lại mật khẩu mới"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Submit Button */}
                        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                          <Link
                            to="/user/profile"
                            className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
                          >
                            Hủy
                          </Link>
                          <button
                            type="submit"
                            disabled={fetching}
                            className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {fetching ? (
                              <>
                                <svg
                                  className="animate-spin h-5 w-5 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                <span>Đang xử lý...</span>
                              </>
                            ) : (
                              <>
                                <LockClosedIcon className="w-5 h-5" />
                                <span>Đổi mật khẩu</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </AvoRedApp>
  );
};
